/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild, OnInit, OnDestroy,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { CloudDestination, Constants } from '../../../common/constants';
import { UserService } from '../../services/user.service';
import { WorkflowHelperService } from '../../services/workflow-helper.service';
import {
  CloudFileInfo,
  CloudRepo,
  FileType,
  OptionListOptions,
} from '../../services/workflow.service';
import {
  MWCloudItem,
  MWSelectedFilesInFolder,
  XaccService,
} from '../../xacc/xacc.service';
import { XaccBrowseModalComponent } from '../../xacc/xacc-browse-modal.component';
import { XrxMultiFileSelectComponent } from './xrx-multi-file-select.component';
import { XdaStringsService } from '../../services/xda-strings.service';

@Component({
  selector: 'xrx-master-file-select',
  templateUrl: './xrx-master-file-select.component.html',
  styleUrls: ['./xrx-master-file-select.component.less'],
  standalone: false,
})
export class XrxMasterFileSelectComponent implements OnInit, OnDestroy {
  @ViewChild(XrxMultiFileSelectComponent)
  inputMultiSelect: XrxMultiFileSelectComponent;

  //Inputs
  @Input() rawFileTypes: FileType;
  @Input() displayErrors: boolean;
  @Input() maxSelection = 10;
  @Input() minSelection = 1;
  @Input() uploadFiles: any;

  @Output() fileDropped = new EventEmitter<object>();

  public isAdvancedPlan: boolean;
  public specialCharactersInCloudFileName: boolean;
  public selectInputRepo: boolean;
  public selectedCloudFolder: MWCloudItem | null;
  public isMultipleInputFiles = true;
  public cloudRepoOptions: CloudRepo[] | null = [];
  public selectedCloudRepo: any;

  cloudRepoFileSelectedSubs: Subscription;
  cloudRepos = Constants.middlewareDestinations;

  input: InputType = {
    uploadFiles: [],
    supportMutipleInputSource: false, // Will be used when supporting files from different input source
    cloudRepos: [], // type: CloudRepo[]
    cloudRepoInputs: [],
    currentCloudRepo: null,
    mergeDocs: [],
    maxSelection: 1,
    minSelection: 1,
    insertPageNumber: 1,
    maxInsertPageNumber: 2147483647,
    insertPageNumberError: '',
    isBatchInput: false,
    sendInput: false,
    sendInterim: true,
    sendCloudInput: false,
    sendCloudInterim: false,
    pauseInterim: false,
    editUsed: false,
    fileTypes: {
      raw: null,
      asAccept: null,
      asString: null,
    },
    selectedPages: '',
    selectedPagesError: '',
    selectedPagesValidation: /^[ ,\-\d]+$/,
    selectedPagesValidationError: 'SDE_USE_ONLY_NUMBERS',
  };

  inputSources: OptionListOptions[] = [
    {
      key: '',
      value: '',
      title: null,
      options: [
        {
          key: 'SDE_LOCAL_DRIVE',
          value: 'SDE_LOCAL_DRIVE',
          title: null,
          options: null,
        },
      ],
    },
    {
      key: 'SDE_CLOUD_REPOSITORIES',
      value: 'SDE_CLOUD_REPOSITORIES',
      title: 'SDE_CLOUD_REPOSITORIES',
      options: this.cloudRepos,
    },
  ];
  selectedInputSource: any = this.inputSources[0]?.options?.[0];
  inputSourceOptions: InputSourceType[] = [
    {
      value: 'SDE_LOCAL_FILE',
      desc: null,
    },
    {
      value: 'SDE_CLOUD_REPO',
      desc: null,
    },
  ];
  inputSource = this.inputSourceOptions[0];

  constructor(
    public activeModal: NgbActiveModal,
    public userService: UserService,
    private modalService: NgbModal,
    public xaccService: XaccService,
    private workflowHelper: WorkflowHelperService,
    private xdaStringsService: XdaStringsService,
  ) {
    this.isAdvancedPlan = userService.isAdvancedPlan;
    this.input.maxSelection = this.maxSelection;
    this.cloudRepoFileSelectedSubs =
      this.xaccService.directoryOrFilesSelected.subscribe((repoOptions) =>
        this.updateCloudRepoOptions(repoOptions),
      );
  }

  ngOnInit(): void {
    if (this.uploadFiles?.length > 0 && this.uploadFiles[0].connector) {
      this.input.cloudRepoInputs = this.uploadFiles;
      this.input.cloudRepos = this.uploadFiles;
      this.inputSource = this.inputSourceOptions[1];
    } else {
      this.input.uploadFiles = this.uploadFiles;
    }
    this.input.fileTypes.raw = this.rawFileTypes;
    this.input.fileTypes.asAccept = `.${this.workflowHelper.fileTypesToStrings(this.rawFileTypes, true).join(',.')},${this.workflowHelper.fileTypesToMIMETypes(this.rawFileTypes).join(',')}`;
    this.input.fileTypes.asString = this.workflowHelper
      .fileTypesToStrings(this.rawFileTypes)
      .join(', ');

    this.setupCloudRepoSource();
  }

  setupCloudRepoSource() {
    // TODO Remove when Cloud Repo is fixed
    this.cloudRepos = [];

    const unsupportedRepos = ['Dropbox', 'Box', 'DocuShare', 'GoogleDrive'];
    this.cloudRepos = this.cloudRepos.filter(
      (repo) => !unsupportedRepos.includes(repo.value),
    );

    if (this.cloudRepos.length) {
      this.inputSources[1].options = this.cloudRepos;
    } else {
      // remove the cloud repo option if there aren't any
      this.inputSources.splice(1, 1);
    }
  }

  /**
   * Select Local or Cloud source for file inputs
   * @param selectedInputSource
   */
  selectInputSource(selectedInputSource: OptionListOptions) {
    this.input.uploadFiles = [];
    this.input.cloudRepoInputs = null;
    if (
      typeof selectedInputSource.value === 'string' &&
      selectedInputSource.value.includes('SDE_LOCAL')
    ) {
      this.inputSource = this.inputSourceOptions[0];
    } else {
      this.selectInputRepo = true;
      this.inputSource = this.inputSourceOptions[1];
      this.selectCloudRepoInput(selectedInputSource);
    }
  }

  /**
   * Select input cloud repo
   * @param cloudRepo
   * @param returning
   */
  selectCloudRepoInput(
    selectedCloudRepo: OptionListOptions,
    returning = false,
  ) {
    // reset cloudFileName
    this.input.cloudRepoInputs = [];
    // pass input fileTypes
    if (
      (selectedCloudRepo.options as unknown as CloudDestination).region !==
      undefined
    ) {
      const cloudRepoOption =
        selectedCloudRepo.options as unknown as CloudDestination;
      this.xaccService.initiateWebLogin(
        {
          connector: selectedCloudRepo.value.toString(),
          region: cloudRepoOption.region,
        },
        returning,
        this.input.fileTypes.asAccept || '',
        this.isMultipleInputFiles,
        this.input.maxSelection,
      );
    } else {
      this.xaccService.initiateWebLogin(
        {
          connector: selectedCloudRepo.value.toString(),
          region: '',
        },
        returning,
        this.input.fileTypes.asAccept || '',
        this.isMultipleInputFiles,
        this.input.maxSelection,
      );
    }
  }

  /**
   * Add or change a cloud input
   * @param cDoc
   */
  addChangeCloudInput(cDoc?: CloudFileInfo) {
    let repoFolders = null;
    if (!cDoc) {
      // Add button clicked
      const selectedRepo = this.input.cloudRepos.find(
        (repo: CloudRepo) =>
          repo.connector === this.selectedInputSource.value &&
          repo.region === this.selectedInputSource.region,
      );
      if (selectedRepo) {
        cDoc = selectedRepo;
        repoFolders = selectedRepo.files;
      } else {
        //login to the selected repo
        this.selectInputSource(this.selectedInputSource);
      }
    } else {
      repoFolders = this.input.cloudRepos.find(
        (repo: CloudRepo) => repo.connector === cDoc?.connector,
      )?.files;
    }

    const selectedFolders = JSON.parse(JSON.stringify(repoFolders));
    const modalRef = this.modalService.open(XaccBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.returning = true;
    modalRef.componentInstance.enableFileTypes = this.input.fileTypes.asAccept;
    modalRef.componentInstance.multiSelect = this.isMultipleInputFiles;
    modalRef.componentInstance.maxSelection = this.input.maxSelection;
    modalRef.componentInstance.returningRepo = {
      connector: cDoc?.connector,
      region: cDoc?.region,
      accessToken: cDoc?.access,
      refreshToken: cDoc?.refresh,
      sitePath: cDoc?.sitePath,
      dir: cDoc?.dir,
      selectedFolders: this.isMultipleInputFiles ? selectedFolders : null,
    };

    modalRef.result
      .then((repoOptions) => {
        this.updateCloudRepoInputs(repoOptions);
      })
      .catch((ex) => {
        if (ex && this.cloudRepoOptions) {
          this.selectCloudRepo(this.cloudRepoOptions[0], true);
        }
      });
  }

  /**
   * Add or change a cloud input
   * @param cDoc
   */
  selectCloudRepo(selectedCloudRepo: CloudRepo, returning = false) {
    if (!selectedCloudRepo) {
      return;
    }

    this.selectedCloudFolder = null;
    this.selectInputRepo = false;
    this.xaccService.initiateWebLogin(
      {
        connector: selectedCloudRepo.connector,
        region: selectedCloudRepo.region,
      },
      returning,
    );
    if (!returning) {
      this.updateCloudRepoOptions({
        connector: selectedCloudRepo.connector,
        region: selectedCloudRepo.region,
      });
    }
  }

  /**
   * enable input files from multiple cloud repos
   * @param repo
   */
  updateCloudRepoInputs(repo: CloudRepo) {
    this.input.currentCloudRepo = repo;
    this.specialCharactersInCloudFileName = false;

    if (!this.input.cloudRepos || this.input.cloudRepos.length === 0) {
      this.input.cloudRepos = [];
    }

    if (this.input.supportMutipleInputSource) {
      let isNewRepo = true;
      for (let i = 0; i < this.input.cloudRepos.length; i++) {
        if (this.input.cloudRepos[i].connector === repo.connector) {
          // update the existing repo
          this.input.cloudRepos[i] = repo;
          isNewRepo = false;
          break;
        }
      }
      if (isNewRepo) {
        this.input.cloudRepos.push(repo);
      }
    } else {
      this.input.cloudRepos = [];
      this.input.cloudRepos.push(repo);
    }

    // Create CloudFileInfo object for each selected cloud file
    this.input.cloudRepoInputs = [];
    if (this.isMultipleInputFiles) {
      // process multiple repos -> multiple folders -> multiple files
      this.input.cloudRepos.forEach((repo: CloudRepo) => {
        // handle multiple selected files. Instantiate a CloudRepoFileInfo object for each selected file.
        if (
          (repo.files as unknown as MWSelectedFilesInFolder[])[0].files !==
          undefined
        ) {
          const cloudFolders =
            repo.files as unknown as MWSelectedFilesInFolder[];
          if (cloudFolders) {
            cloudFolders.forEach((folder) => {
              folder.files.forEach((file) => {
                const cloudFile: CloudFileInfo = Object.assign(
                  { file: null, insertPageNumber: 1 },
                  repo,
                );
                //cloudFile.sitePath = folder.sitePath;
                cloudFile.dir = folder.dir;
                cloudFile.file = file;
                cloudFile.fileFormat = this.getFileFormat(file.name) || '';
                cloudFile.outputFileName = file.name.substr(
                  0,
                  file.name.lastIndexOf('.'),
                );
                cloudFile.insertPageNumber = 1;
                this.input.cloudRepoInputs?.push(cloudFile);
              });
            });
          }
        }
        // use the first file for default output file name creation
        // check 'file' in case no file is selected
        if (
          this.input.cloudRepoInputs?.length &&
          this.input.cloudRepoInputs[0].file
        ) {
          this.handleSelectedFileFromCloud(
            this.input.cloudRepoInputs[0].file.name,
          );
        }
        // check for invalid characters in file names if multiple files have been selected
        if (this.input.cloudRepoInputs?.length) {
          for (let i = 0; i < this.input.cloudRepoInputs.length; i++) {
            if (
              this.hasSpecialCharacters(
                this.input.cloudRepoInputs[i].file?.name || '',
              )
            ) {
              this.specialCharactersInCloudFileName = true;
              break;
            }
          }
        }
      });
    }
    // single file from a cloud repo
    else {
      const cloudFile: CloudFileInfo = Object.assign(
        {},
        this.input.currentCloudRepo,
      );
      if ((repo.files as unknown as MWCloudItem).id !== undefined) {
        cloudFile.file = this.input.currentCloudRepo
          .files as unknown as MWCloudItem;
      }
      // check 'file' in case no file is selected
      if (cloudFile.file) {
        const fileName = cloudFile.file.name;
        cloudFile.fileFormat = this.getFileFormat(fileName) || '';
        this.input.cloudRepoInputs.push(cloudFile);
        this.handleSelectedFileFromCloud(fileName);
      }
    }

    if (this.input.cloudRepoInputs) {
      this.fileDropped.emit({
        files: this.input.cloudRepoInputs,
        isValid: true,
        isCloud: true,
      });
    }
    this.input.isBatchInput = this.input.cloudRepoInputs.length > 1;
  }

  checkFileNameAndFormat(fileName: string, setOutputFileName = true) {
    if (setOutputFileName) {
      //this.output.fileName = fileName.substr(0, fileName.lastIndexOf("."));
    }

    const fileFormat = this.getFileFormat(fileName);
    if (!fileFormat) {
      //  this.fileFormatNotSupported = true;
      //  this.isGoodToGo = false;
      return null;
    }

    if (this.input.fileTypes.asAccept?.indexOf(fileFormat) === -1) {
      //  this.fileFormatNotSupported = true;
      //this.isGoodToGo = false;
      return null;
    }

    return fileFormat;
  }

  handleSelectedFileFromCloud(fileName: string) {
    if (!this.checkFileNameAndFormat(fileName)) {
      return false;
    }
    return true;
  }

  getFileFormat(fileName: string) {
    const index = fileName.lastIndexOf('.');
    if (index === -1) {
      return null;
    }
    return fileName.substr(index + 1).toLowerCase();
  }

  updateCloudRepoOptions(repoOption: CloudRepo) {
    // check access instead of file as the user may not select any files.
    if (this.selectInputRepo) {
      return this.updateCloudRepoInputs(repoOption);
    }

    if (repoOption == null) {
      this.cloudRepoOptions = null;
      this.selectedCloudFolder = null;
    } else {
      this.cloudRepoOptions = [repoOption];
      const sitePath =
        repoOption.sitePath && repoOption.sitePath.length
          ? repoOption.sitePath[0]
          : null;
      const cloudRepoPath = repoOption.dir || sitePath;
      if (cloudRepoPath) {
        // Set to value
        this.selectedCloudFolder = Object.assign({}, cloudRepoPath);
        if (cloudRepoPath.name) {
          // Remove Trailing /
          if (this.selectedCloudFolder.name.endsWith('/')) {
            this.selectedCloudFolder.name = this.selectedCloudFolder.name.slice(
              0,
              -1,
            );
          }
          // Get last segment
          this.selectedCloudFolder.name =
            this.selectedCloudFolder.name.substring(
              this.selectedCloudFolder.name.lastIndexOf('/') + 1,
            );
        } else {
          // Root folder selected, display repo name
          this.selectedCloudFolder.name = this.selectedCloudRepo.key;
        }
      }
    }
  }

  handleUploadFileInputs(prams: { files: CloudFileInfo[] }) {
    this.input.uploadFiles = [];
    this.input.uploadFiles.push(...prams.files);

    this.input.uploadFiles.forEach((f, i) => {
      f.fileFormat = this.checkFileNameAndFormat(f.name || '', i === 0) || '';
      f.outputFileName = f.name?.substr(0, f.name.lastIndexOf('.'));
    });

    this.input.isBatchInput = this.input.uploadFiles.length > 1;

    this.fileDropped.emit({ files: this.input.uploadFiles, isValid: true });
  }

  validateInput() {
    return this.inputMultiSelect?.validateInput();
  }

  /**
   * Used to determind if string contains special characters
   */
  hasSpecialCharacters(stringToTest: string) {
    return !/^[^"?;<>|:'*%^&#+`\\\/]+$/.test(stringToTest);
  }

  ngOnDestroy() {
    this.cloudRepoFileSelectedSubs?.unsubscribe();
  }
}

interface InputType {
  uploadFiles: CloudFileInfo[];
  supportMutipleInputSource: boolean;
  cloudRepos: CloudRepo[];
  cloudRepoInputs: CloudFileInfo[] | null;
  currentCloudRepo: CloudRepo | null;
  mergeDocs: string[];
  maxSelection: number;
  minSelection: number;
  insertPageNumber: number;
  maxInsertPageNumber: number;
  insertPageNumberError: string;
  isBatchInput: boolean;
  sendInput: boolean;
  sendInterim: boolean;
  sendCloudInput: boolean;
  sendCloudInterim: boolean;
  pauseInterim: boolean;
  editUsed: boolean;
  fileTypes: {
    raw: FileType | null;
    asAccept: string | null;
    asString: string | null;
  };
  selectedPages: string;
  selectedPagesError: string;
  selectedPagesValidation: RegExp;
  selectedPagesValidationError: string;
}

interface InputSourceType {
  value: string;
  desc: string | null;
}
